import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { MdSend } from "react-icons/md";
import FileSelector from "components/FileSelector/FileSelector";
import Attachment from "components/Attachment/Attachment";
import { MdAttachFile } from "react-icons/md";
import _colors from "styles/colors.module.scss";
import { UserGroup } from "constants/UserConstants";
import CannedMessageService from "services/CannedMessageService";
import DealerLocationService from "services/DealerLocationService";
import { SuggestionType } from "constants/MessageConstants";
import MessageSuggestionReceiver from "services/MessageSuggestionReceiver";
import { getFileObjectFromUrl } from "utils/Common";
import { MdClear } from "react-icons/md";
const { lightBlue } = _colors;

class ChatTextArea extends Component {
  static propTypes = {
    onSendNewTextMessage: PropTypes.func.isRequired,
    onSendNewAttachment: PropTypes.func.isRequired,
    onSendNewLocationMessage: PropTypes.func.isRequired,
    agentGroup: PropTypes.string.isRequired,
    isActiveConversation: PropTypes.bool,
    attachments: PropTypes.array,
  };

  static defaultProps = {
    isActiveConversation: false,
    attachments: [],
  };

  static VALID_MESSAGE_REGEX = /^[a-zA-Z0-9 .,?/!]*$/;

  constructor() {
    super();
    this.state = {
      showFileSelector: false,
      attachedFile: null,
      showAttachIcon: true,
      message: "",
      suggestionInfo: null,
      dealerLocationMessage: null,
      inputValidationError: "",
    };
  }

  componentDidMount() {
    this.cannedMessageSubscription = CannedMessageService.listen().subscribe(
      this.onNewCannedMessage
    );
    this.dealerLocationSubscription = DealerLocationService.listen().subscribe(
      this.onNewDealerLocation
    );
    this.suggestionReceiverSubscription = MessageSuggestionReceiver.listen().subscribe(
      this.onReceiptOfNewSuggestion
    );
  }

  componentWillUnmount() {
    if (this.cannedMessageSubscription) {
      this.cannedMessageSubscription.unsubscribe();
    }
    if (this.suggestionReceiverSubscription) {
      this.suggestionReceiverSubscription.unsubscribe();
    }
    if (this.dealerLocationSubscription) {
      this.dealerLocationSubscription.unsubscribe();
    }
  }

  render() {
    const {
      message,
      attachedFile,
      showAttachment,
      showAttachIcon,
      dealerLocationMessage,
      inputValidationError,
    } = this.state;
    const { isChatFreezed, agentGroup } = this.props;
    const formDisabledCls = isChatFreezed ? "disabled" : "";
    return (
      <div className="form-ctr">
        <form
          className={`chat-form ${formDisabledCls}`}
          onSubmit={this.onSendNewMessage}
        >
          <FileSelector
            showSelector={this.state.showFileSelector}
            onFileSelectorClose={this.onFileSelectorClose}
            attachSelectedFile={this.copySelectedFileToTextArea}
          />
          <div className="textarea-ctr">
            <textarea
              className="chat-msg-input"
              value={message}
              type="text"
              onChange={this.onMessageChange}
              placeholder="Type message here..."
              onKeyPress={this.onChatKeyPress}
              disabled={isChatFreezed || attachedFile || dealerLocationMessage}
            />
            {showAttachIcon && agentGroup !== UserGroup.SERVICE && (
              <MdAttachFile
                color={lightBlue}
                className="file-attach-icon"
                size="2em"
                onClick={this.renderFileSelector}
              />
            )}
            {dealerLocationMessage && (
              <div
                className="cross"
                onClick={this.handleRemoveDealerLocationMessage}
              >
                <MdClear size="1.2em" />
              </div>
            )}
          </div>
          {inputValidationError && (
            <p className="chat-input-error">{inputValidationError}</p>
          )}
          <div className="attachment-and-send-button-ctr">
            {showAttachment && attachedFile && (
              <Attachment
                fileName={attachedFile.name}
                removeAttachedFile={this.handleRemoveAttachedFile}
              />
            )}
            <button
              className="msg-send-btn"
              type="submit"
              onClick={this.onSendNewMessage}
              disabled={this.isSendDisabled()}
            >
              <MdSend className="chat-msg-send" size="2em" />
            </button>
          </div>
        </form>
      </div>
    );
  }

  getFileFromAttachmentsOrUrl = (fileUrl) => {
    const { attachments } = this.props;
    const attachmentOfFileUrl = attachments.find((a) => a.fileUrl === fileUrl);
    if (attachmentOfFileUrl) {
      return attachmentOfFileUrl;
    }
    return getFileObjectFromUrl(fileUrl);
  };

  onMessageChange = (e) => {
    const newValue = e.target.value;
    if (ChatTextArea.VALID_MESSAGE_REGEX.test(newValue)) {
      this.setState({ message: newValue, inputValidationError: "" });
    } else {
      const sanitized = newValue.replace(/[^a-zA-Z0-9 .,?/!]/g, "");
      this.setState({
        message: sanitized,
        inputValidationError: "Only letters, numbers, and . , ? / ! are allowed.",
      });
    }
  };

  onNewCannedMessage = (message) => {
    if (this.props.isActiveConversation && !this.state.attachedFile) {
      this.setState({
        message,
      });
    }
  };

  onNewDealerLocation = (locationMessage) => {
    if (this.props.isActiveConversation && !this.state.attachedFile) {
      this.setState({
        message: locationMessage.messageToCopyToChatTextArea,
        dealerLocationMessage: locationMessage,
        showAttachIcon: false,
      });
    }
  };

  onReceiptOfNewSuggestion = (suggestion) => {
    if (!this.props.isActiveConversation || this.state.attachedFile) return;
    if (suggestion.suggestionType === SuggestionType.TEXT) {
      this.setState({
        message: suggestion.content,
        suggestionInfo: suggestion.suggestionInfo,
      });
    }
    if (suggestion.suggestionType === SuggestionType.ATTACHMENT) {
      this.copySelectedFileToTextArea(
        this.getFileFromAttachmentsOrUrl(suggestion.content)
      );
      this.setState({
        suggestionInfo: suggestion.suggestionInfo,
      });
    }
  };

  copySelectedFileToTextArea = (selectedFile) => {
    this.setState({
      attachedFile: selectedFile,
      showFileSelector: false,
      showAttachIcon: true,
      showAttachment: true,
      message: "",
    });
  };

  onFileSelectorClose = () => {
    this.setState({
      showFileSelector: false,
      showAttachIcon: true,
    });
  };

  onChatKeyPress = (event) => {
    if (event.which === 13 && !event.shiftKey) {
      this.onSendNewMessage(event);
    }
  };

  isSendDisabled = () => {
    const { isChatFreezed } = this.props;
    const { message } = this.state;
    return (
      (message.trim().length === 0 && this.state.attachedFile === null) ||
      isChatFreezed
    );
  };

  onSendNewMessage = (e) => {
    e.preventDefault();
    if (this.isSendDisabled()) return;
    const {
      attachedFile,
      message,
      suggestionInfo,
      dealerLocationMessage,
    } = this.state;
    if (!attachedFile && !dealerLocationMessage && !ChatTextArea.VALID_MESSAGE_REGEX.test(message)) {
      this.setState({ inputValidationError: "Only letters, numbers, and . , ? / ! are allowed." });
      return;
    }
    const {
      onSendNewAttachment,
      onSendNewTextMessage,
      onSendNewLocationMessage,
    } = this.props;
    if (attachedFile !== null) {
      onSendNewAttachment(attachedFile, suggestionInfo);
    } else if (dealerLocationMessage && dealerLocationMessage !== null) {
      onSendNewLocationMessage(dealerLocationMessage, suggestionInfo);
    } else {
      onSendNewTextMessage(message, suggestionInfo);
    }
    this.setState({ message: "", suggestionInfo: null, inputValidationError: "" });
    this.handleRemoveAttachedFile();
    this.handleRemoveDealerLocationMessage();
  };

  handleRemoveAttachedFile = () => {
    this.setState({ attachedFile: null, showAttachment: false });
  };

  handleRemoveDealerLocationMessage = () => {
    this.setState({ dealerLocationMessage: null, message: "", showAttachIcon: true });
  };

  renderFileSelector = () => {
    this.setState({ showFileSelector: true, showAttachIcon: false });
  };
}

const mapStateToProps = (state) => ({
  attachments: state.attachments,
});

export default connect(mapStateToProps)(ChatTextArea);
